/**
 * pcard.js — the product card's add-to-cart state machine.
 *
 * Every `.c-pcard` surface runs on this one module: editorial and compact cards,
 * the PDP buy box and sticky bar, buy-again cards. Docs: docs/features/product-cards.md.
 *
 * It lives here, loaded by layout/theme.liquid on every page, and NOT in a
 * `{% javascript %}` tag in snippets/molecule-product-card.liquid where it was
 * written. Shopify runs a snippet's `{% javascript %}` only on pages whose server
 * render included that snippet (the `data-snippets` list on #snippets-script),
 * while card controls also reach pages that never render it: the PDP and
 * buy-again render pcard-add-button / pcard-stepper themselves, and the search
 * panel and PDP recommendations arrive by fetch. Until 12 Oct the header's
 * search panel rendered cards on every page, which loaded this code everywhere
 * by accident; B2C-321 made the panel lazy and every one of those controls went
 * dead (B2C-326). See docs/patterns.md.
 */
/* =========================================================================
   PCARD — Add-to-cart state machine
   Handles: idle -> busy -> stepper -> zero-revert
   Event delegation on document — works for any number of cards.
   ========================================================================= */
(function () {
  'use strict';

  var BUSY_MIN_MS = 280;
  var DEBOUNCE_MS = 300;
  var ERROR_FLASH_MS = 1500;

  /* SVG icons for stepper dec button */
  var TRASH_SVG = '<span class="c-icon" style="width:14px;height:14px;" aria-hidden="true"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2"/><path d="M5 6v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6"/><path d="M10 11v6M14 11v6"/></svg></span>';
  var NDASH_HTML = '<span aria-hidden="true">\u2013</span>';

  /* ---- Debounce map keyed by variant ID ---- */
  var debounceTimers = {};
  var pendingQty = {};
  var confirmedQty = {};

  /* ---- Helper: dispatch cart:update for Horizon CartIcon ---- */
  function emitCartUpdate(itemCount) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: { data: { itemCount: itemCount, source: 'pcard' } }
    }));
  }

  /* ---- Helper: read translated labels from card data attributes ---- */
  function getLabels(card) {
    return {
      quantity: card.dataset.labelQuantity || 'Quantity',
      decrease: card.dataset.labelDecrease || 'Decrease quantity',
      increase: card.dataset.labelIncrease || 'Increase quantity',
      addToBasket: card.dataset.labelAddToBasket || 'Add to basket'
    };
  }

  /* ---- Helper: set dec button to trash or ndash based on qty ---- */
  function updateDecIcon(stepper, qty) {
    var decBtn = stepper.querySelector('[data-step="-1"]');
    if (!decBtn) return;
    if (qty <= 1) {
      decBtn.innerHTML = TRASH_SVG;
      decBtn.classList.add('is-trash');
      decBtn.setAttribute('aria-label', 'Remove item');
    } else {
      decBtn.innerHTML = NDASH_HTML;
      decBtn.classList.remove('is-trash');
    }
  }

  /* ---- Helper: build stepper DOM (JS-side, for after cart add) ---- */
  function buildStepper(card, variantId, lineKey, qty) {
    var labels = getLabels(card);

    var form = document.createElement('form');
    form.action = '/cart/change';
    form.method = 'POST';
    form.className = 'c-pcard__stepper-form';

    var hiddenId = document.createElement('input');
    hiddenId.type = 'hidden';
    hiddenId.name = 'id';
    hiddenId.value = lineKey;
    form.appendChild(hiddenId);

    var wrap = document.createElement('div');
    var stepperVariant = card.dataset.stepperVariant;
    wrap.className = stepperVariant ? 'c-pcard__stepper c-pcard__stepper--' + stepperVariant : 'c-pcard__stepper';
    wrap.setAttribute('dir', 'ltr');
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', labels.quantity);
    wrap.dataset.variantId = variantId;
    wrap.dataset.lineKey = lineKey;

    var minus = document.createElement('button');
    minus.type = 'button';
    minus.dataset.step = '-1';
    if (qty <= 1) {
      minus.innerHTML = TRASH_SVG;
      minus.classList.add('is-trash');
      minus.setAttribute('aria-label', 'Remove item');
    } else {
      minus.setAttribute('aria-label', labels.decrease);
      minus.innerHTML = '<span aria-hidden="true">\u2013</span>';
    }

    var qtySpan = document.createElement('span');
    qtySpan.className = 'c-pcard__stepper-qty';
    qtySpan.setAttribute('aria-live', 'polite');
    qtySpan.setAttribute('aria-atomic', 'true');
    qtySpan.textContent = qty;

    var plus = document.createElement('button');
    plus.type = 'button';
    plus.dataset.step = '+1';
    plus.setAttribute('aria-label', labels.increase);
    plus.innerHTML = '<span aria-hidden="true">+</span>';

    wrap.appendChild(minus);
    wrap.appendChild(qtySpan);
    wrap.appendChild(plus);
    form.appendChild(wrap);

    return form;
  }

  /* ---- Helper: build add button DOM (for zero-revert) ---- */
  function buildAddButton(card, variantId) {
    var labels = getLabels(card);
    var addLabel = card.dataset.labelAdd || 'Add';

    var form = document.createElement('form');
    form.action = '/cart/add';
    form.method = 'POST';
    form.className = 'c-pcard__add-form';

    var hiddenId = document.createElement('input');
    hiddenId.type = 'hidden';
    hiddenId.name = 'id';
    hiddenId.value = variantId;
    form.appendChild(hiddenId);

    var hiddenQty = document.createElement('input');
    hiddenQty.type = 'hidden';
    hiddenQty.name = 'quantity';
    hiddenQty.value = '1';
    form.appendChild(hiddenQty);

    var btn = document.createElement('button');
    btn.type = 'submit';
    btn.className = 'c-pcard__add';
    btn.setAttribute('aria-label', labels.addToBasket);
    btn.innerHTML = '<span class="c-pcard__add-glyph" aria-hidden="true">+</span>' +
                    '<span class="c-pcard__add-label">' + addLabel + '</span>';
    form.appendChild(btn);

    return form;
  }

  /* ---- Gelato (B2C-239): aggregate stepper + pick-button builders -------- */
  /* Gelato variants hold an AGGREGATE qty across N combo lines. The card never
     renders a stepper: count 0 = "Pick flavours", count >=1 = count chip + "Add
     another" (opens the modal for a new combo). Removal of a specific combo lives
     in the cart, so there is no minus/trash on the card at any count. */

  function buildGelatoPickButton(card, count) {
    count = count || 0;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'c-pcard__pick' + (count > 0 ? ' c-pcard__pick--has-count' : '');
    btn.setAttribute('data-gelato-open', '');
    btn.setAttribute('data-gelato-sku', card.dataset.gelatoSku || '');
    btn.setAttribute('data-variant-id', card.dataset.variantId || '');
    btn.setAttribute('data-product-title', card.dataset.productTitle || '');

    var fullText, shortText;
    if (count > 0) {
      var ariaTpl = card.dataset.labelAddAnotherAria || 'Add another, __COUNT__ in basket';
      btn.setAttribute('aria-label', ariaTpl.replace('__COUNT__', count));

      var chip = document.createElement('span');
      chip.className = 'c-pcard__pick-count';
      chip.setAttribute('aria-hidden', 'true');
      chip.textContent = count;
      btn.appendChild(chip);

      fullText = card.dataset.labelAddAnother || 'Add another';
      shortText = card.dataset.labelAddAnotherShort || 'Add';
    } else {
      fullText = card.dataset.labelPick || 'Pick flavours';
      shortText = card.dataset.labelPickShort || 'Pick';
    }

    var full = document.createElement('span');
    full.className = 'c-pcard__pick-label';
    full.textContent = fullText;
    btn.appendChild(full);

    var short = document.createElement('span');
    short.className = 'c-pcard__pick-label--short';
    short.textContent = shortText;
    btn.appendChild(short);

    return btn;
  }

  /* Fallback mirror of CasinettoCart.buildVariantMap for load-order safety. */
  function variantMapOf(cart) {
    if (window.CasinettoCart && window.CasinettoCart.buildVariantMap) {
      return window.CasinettoCart.buildVariantMap(cart);
    }
    var map = {};
    (cart.items || []).forEach(function (item) {
      var v = String(item.variant_id);
      if (!map[v]) { map[v] = { qty: 0, lineCount: 0, key: null }; }
      map[v].qty += item.quantity;
      map[v].lineCount += 1;
      map[v].key = (map[v].lineCount === 1) ? item.key : null;
    });
    return map;
  }

  /* ---- Helper: flash error state on button ---- */
  function flashError(btn) {
    btn.removeAttribute('data-state');
    btn.classList.add('c-pcard__add--error');
    setTimeout(function () {
      btn.classList.remove('c-pcard__add--error');
    }, ERROR_FLASH_MS);
  }

  /* ---- Helper: get card article element ---- */
  function getCard(el) {
    return el.closest('.c-pcard');
  }

  /* ---- Helper: the quantity ceilings this card knows about (B2C-284) ----
     All three are emitted by snippets/quantity-ceiling-attrs.liquid and are
     ABSENT when they do not apply — an absent `data-max-qty` means no cap, an
     absent `data-stock` means no stock ceiling known (NOT zero). The wrapper
     normalises them; nothing here interprets them. */
  function ceilingOf(card) {
    return {
      productId: card.dataset.productId,
      cap: card.dataset.maxQty,
      stock: card.dataset.stock,
      orderLimitMessage: card.dataset.msgOrderLimit
    };
  }

  /* Every cart mutation on this page goes through the wrapper — there is no
     raw fetch left. If it somehow has not loaded, fail rather than reopening a
     second, ungated path. */
  function cartApi() {
    if (window.CasinettoCart && window.CasinettoCart.add) {
      return window.CasinettoCart;
    }
    return null;
  }

  /* ---- ADD BUTTON CLICK ---- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.c-pcard__add');
    if (!btn) return;
    if (btn.disabled) return;

    /* Double-tap guard (UX-B2) */
    if (btn.dataset.state === 'busy') return;
    btn.dataset.state = 'busy';

    e.preventDefault();

    var card = getCard(btn);
    if (!card) return;
    var variantId = card.dataset.variantId;
    var busyStart = Date.now();
    var ceilings = ceilingOf(card);

    var api = cartApi();
    if (!api) { flashError(btn); return; }

    api.add({
      id: variantId,
      quantity: 1,
      productId: ceilings.productId,
      cap: ceilings.cap,
      stock: ceilings.stock,
      orderLimitMessage: ceilings.orderLimitMessage
    })
    .then(function (result) {
      /* Enforce minimum busy duration */
      var elapsed = Date.now() - busyStart;
      var remaining = Math.max(0, BUSY_MIN_MS - elapsed);

      return new Promise(function (resolve) {
        setTimeout(function () { resolve(result); }, remaining);
      });
    })
    .then(function (result) {
      if (!result.ok) {
        /* Refused at a ceiling. The wrapper has already explained which one, so
           the card just stands down: no stepper is built, the count does not
           move, and this is NOT flashError — nothing failed. */
        btn.removeAttribute('data-state');
        return;
      }

      /* Build stepper, replace the add button's parent form */
      var cartDiv = card.querySelector('.c-pcard__cart');
      if (!cartDiv) return;

      var lineKey = result.item.key;
      card.dataset.lineKey = lineKey;

      var stepperForm = buildStepper(card, variantId, lineKey, 1);
      /* Clear the cart div and insert stepper */
      cartDiv.innerHTML = '';
      cartDiv.appendChild(stepperForm);

      /* Stepper appearing is sufficient feedback — no auto-focus needed */

      /* Set confirmed qty */
      confirmedQty[variantId] = 1;
      pendingQty[variantId] = 1;

      /* The wrapper already re-read the cart, so the header count comes from
         its result rather than a second round trip. */
      if (result.cart) emitCartUpdate(result.cart.item_count);
    })
    .catch(function () {
      /* Genuine network failure only — a ceiling never reaches here. */
      flashError(btn);
    });
  });

  /* ---- STEPPER BUTTON CLICK ---- */
  document.addEventListener('click', function (e) {
    var stepBtn = e.target.closest('.c-pcard__stepper [data-step]');
    if (!stepBtn) return;
    e.preventDefault();

    var stepper = stepBtn.closest('.c-pcard__stepper');
    if (!stepper) return;

    var card = getCard(stepper);
    if (!card) return;

    var variantId = card.dataset.variantId;
    var lineKey = stepper.dataset.lineKey || card.dataset.lineKey;
    var qtySpan = stepper.querySelector('.c-pcard__stepper-qty');
    var step = parseInt(stepBtn.dataset.step, 10);
    var ceilings = ceilingOf(card);

    var api = cartApi();
    if (!api) return;

    /* The base is the PENDING quantity, not what is painted. The ceiling
       pre-flight below is asynchronous, so two fast presses would otherwise
       both read the same painted number and the second would be lost. */
    var base = pendingQty[variantId];
    if (base === undefined) base = parseInt(qtySpan.textContent, 10) || 0;
    var newQty = base + step;
    if (newQty < 0) newQty = 0;
    if (newQty === base) return;

    if (newQty === 0) {
      /* Zero-revert: replace stepper with add button. A decrease can breach no
         ceiling, so it is applied optimistically exactly as before. */
      qtySpan.textContent = '0';

      var cartDiv = card.querySelector('.c-pcard__cart');
      if (!cartDiv) return;

      var addForm = buildAddButton(card, variantId);
      cartDiv.innerHTML = '';
      cartDiv.appendChild(addForm);

      /* No auto-focus — visual transition is sufficient feedback */

      /* POST removal immediately */
      api.change({ key: lineKey, quantity: 0 })
      .then(function (result) {
        card.dataset.lineKey = '';
        delete confirmedQty[variantId];
        delete pendingQty[variantId];
        if (result.cart) emitCartUpdate(result.cart.item_count);
      })
      .catch(function () {
        /* Revert: rebuild stepper with confirmed qty */
        var revertQty = confirmedQty[variantId] || 1;
        var revertForm = buildStepper(card, variantId, lineKey, revertQty);
        cartDiv.innerHTML = '';
        cartDiv.appendChild(revertForm);
        pendingQty[variantId] = revertQty;
      });

      return;
    }

    /* Reserve the slot before the asynchronous check so the next press counts
       from here; restored on a refusal. */
    pendingQty[variantId] = newQty;

    /* THE PRE-FLIGHT RUNS ON THE PRESS, not on the debounced send: a refusal
       has to be instant, and the number must never tick up and fall back.
       With no cap and no known stock this resolves in a microtask and nothing
       about the old timing changes. */
    api.check({
      mode: 'change',
      key: lineKey,
      quantity: newQty,
      productId: ceilings.productId,
      cap: ceilings.cap,
      stock: ceilings.stock,
      orderLimitMessage: ceilings.orderLimitMessage
    })
    .then(function (ceiling) {
      if (ceiling) {
        /* Refused. The wrapper fired the toast; the displayed quantity never
           moved and there is nothing to undo but the reservation. */
        pendingQty[variantId] = base;
        return;
      }

      /* Optimistic UI update */
      var liveStepper = card.querySelector('.c-pcard__stepper') || stepper;
      var liveQty = liveStepper.querySelector('.c-pcard__stepper-qty') || qtySpan;
      liveQty.textContent = newQty;

      /* Update dec button icon (trash at 1, ndash otherwise) */
      updateDecIcon(liveStepper, newQty);

      /* Debounce the API call (300ms trailing) */
      if (debounceTimers[variantId]) {
        clearTimeout(debounceTimers[variantId]);
      }

      debounceTimers[variantId] = setTimeout(function () {
        var finalQty = pendingQty[variantId];

        api.change({
          key: lineKey,
          quantity: finalQty,
          productId: ceilings.productId,
          cap: ceilings.cap,
          stock: ceilings.stock,
          orderLimitMessage: ceilings.orderLimitMessage
        })
        .then(function (result) {
          var cartData = result.cart;
          var reason = result.ceiling && result.ceiling.reason;

          if (reason === 'SOLD_OUT_REMOVED') {
            /* Shopify removed the line under us. Back to the add button. */
            var soldDiv = card.querySelector('.c-pcard__cart');
            if (soldDiv) {
              soldDiv.innerHTML = '';
              soldDiv.appendChild(buildAddButton(card, variantId));
            }
            card.dataset.lineKey = '';
            delete confirmedQty[variantId];
            delete pendingQty[variantId];
            if (cartData) emitCartUpdate(cartData.item_count);
            return;
          }

          /* A clamp is the authority on what the quantity now is. */
          var appliedQty = finalQty;
          if (reason === 'STOCK_CLAMPED') { appliedQty = result.ceiling.limit; }

          confirmedQty[variantId] = appliedQty;
          pendingQty[variantId] = appliedQty;

          /* Re-query the stepper to avoid a stale reference */
          var currentStepper = card.querySelector('.c-pcard__stepper');
          if (currentStepper) {
            var currentQtySpan = currentStepper.querySelector('.c-pcard__stepper-qty');
            if (currentQtySpan && parseInt(currentQtySpan.textContent, 10) !== appliedQty) {
              currentQtySpan.textContent = appliedQty;
            }
            updateDecIcon(currentStepper, appliedQty);
          }

          /* Update line key if it changed */
          var updatedItem = cartData && cartData.items.find(function (item) {
            return String(item.variant_id) === String(variantId);
          });
          if (updatedItem) {
            if (currentStepper) currentStepper.dataset.lineKey = updatedItem.key;
            card.dataset.lineKey = updatedItem.key;
          }

          if (cartData) emitCartUpdate(cartData.item_count);
        })
        .catch(function () {
          /* Revert to last confirmed qty. Network failure only — a ceiling
             resolves, it does not reject. */
          var revertQty = confirmedQty[variantId] || 1;
          var revertStepper = card.querySelector('.c-pcard__stepper');
          var revertSpan = revertStepper && revertStepper.querySelector('.c-pcard__stepper-qty');
          if (revertSpan) revertSpan.textContent = revertQty;
          pendingQty[variantId] = revertQty;
        });
      }, DEBOUNCE_MS);
    });
  });

  /* ---- EXTERNAL CART SYNC ---- */
  /* When an item is removed via the cart drawer (or any other source),
     revert stale steppers back to add buttons. */
  var cartSyncBusy = false;
  document.addEventListener('cart:update', function (e) {
    /* Skip events we fired ourselves */
    if (e.detail && e.detail.data && e.detail.data.source === 'pcard') return;
    if (cartSyncBusy) return;
    cartSyncBusy = true;

    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        /* Aggregate map: variant_id -> { qty: SUM across lines, lineCount, key } */
        var cartMap = variantMapOf(cart);

        var cards = document.querySelectorAll('.c-pcard');
        cards.forEach(function (card) {
          var variantId = card.dataset.variantId;
          if (!variantId) return;

          var cartDiv = card.querySelector('.c-pcard__cart');
          if (!cartDiv) return;

          var entry = cartMap[variantId];

          if (card.dataset.gelato === '1') {
            /* Gelato: always the pick button, reflecting the AGGREGATE qty across
               combo lines (count chip + "Add another" at >=1). Reconcile on qty
               only — never line_key. Removal of a specific combo lives in the cart. */
            var gQty = entry ? entry.qty : 0;
            cartDiv.innerHTML = '';
            cartDiv.appendChild(buildGelatoPickButton(card, gQty));
            if (gQty > 0) {
              confirmedQty[variantId] = gQty;
              pendingQty[variantId] = gQty;
            } else {
              delete confirmedQty[variantId];
              delete pendingQty[variantId];
            }
            card.dataset.lineKey = '';
            return;
          }

          /* Non-gelato — existing behaviour, now reading the aggregate entry */
          var stepper = cartDiv.querySelector('.c-pcard__stepper');
          if (stepper && !entry) {
            /* Item was removed externally — revert to add button */
            var addForm = buildAddButton(card, variantId);
            cartDiv.innerHTML = '';
            cartDiv.appendChild(addForm);
            card.dataset.lineKey = '';
            delete confirmedQty[variantId];
            delete pendingQty[variantId];
          } else if (stepper && entry) {
            /* Item still in cart — sync qty and line key */
            var qtySpan = stepper.querySelector('.c-pcard__stepper-qty');
            if (qtySpan && parseInt(qtySpan.textContent, 10) !== entry.qty) {
              qtySpan.textContent = entry.qty;
              updateDecIcon(stepper, entry.qty);
              confirmedQty[variantId] = entry.qty;
              pendingQty[variantId] = entry.qty;
            }
            stepper.dataset.lineKey = entry.key;
            card.dataset.lineKey = entry.key;
          } else if (!stepper && entry) {
            /* Item added EXTERNALLY — build the stepper where the add button is.
               This branch was missing, and its absence was not a gap so much as
               an assumption that expired: this handler was written for "removed
               via the cart drawer", where the only possible transition was
               stepper -> button. Nothing could add a line the page did not know
               about. Cross-device cart sync makes that ordinary — another device
               adds, this page adopts — and the card went on offering "Add to
               cart" for a line the account's cart already held. Which is not
               cosmetic: clicking it sends an absolute quantity computed from a
               view that was already wrong. */
            var newStepper = buildStepper(card, variantId, entry.key, entry.qty);
            cartDiv.innerHTML = '';
            cartDiv.appendChild(newStepper);
            card.dataset.lineKey = entry.key;
            confirmedQty[variantId] = entry.qty;
            pendingQty[variantId] = entry.qty;
          }
        });
      })
      .finally(function () { cartSyncBusy = false; });
  });

  /* ---- Prevent no-JS form submission when JS is active ---- */
  document.addEventListener('submit', function (e) {
    if (e.target.closest('.c-pcard__stepper-form')) {
      e.preventDefault();
    }
    if (e.target.closest('.c-pcard__cart form[action="/cart/add"]')) {
      /* Only prevent if the button has JS handling (i.e., not disabled/sold-out) */
      var btn = e.target.querySelector('.c-pcard__add:not(.c-pcard__add--disabled)');
      if (btn) {
        e.preventDefault();
      }
    }
  });
})();
